// APFilterPanel — the standing v4 list-page filters convention (dev decision,
// post-review round): an end-aligned "Filters" trigger with an active-count
// badge toggling an inline PUSH panel beside the table (the table shrinks; no
// backdrop, the list stays visible and interactive). Filters apply LIVE on
// change (v3 `triggerSearchOnValuesChange` parity) — Reset is the only action
// and sits in the panel HEADER row so it stays reachable while the body
// scrolls. On viewports below `lg` the panel falls back to an overlay Sheet
// from the end side (no room to push). APDataTable wires this up via its
// `filters` prop — see the Features page for the template.
import { CloseCircleLinear, FilterLinear } from '@assessprep/icons/solar';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/cn';
import { useSidebarAutoCollapse } from './useSidebarAutoCollapse';

const LG_BREAKPOINT = 1024; // Tailwind `lg`
const DESKTOP_QUERY = `(min-width: ${LG_BREAKPOINT}px)`;

// Synchronous first-render value is load-bearing: a defaultOpen panel that
// first renders with a wrong `false` mounts the OPEN overlay Sheet for one
// frame before the effect corrects the branch (the library explore→search
// "drawer flash"). Exported for sibling push panels (LibraryPreviewPanel).
export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
};

// Transitions arm one frame AFTER mount: a mount-time-open panel (defaultOpen)
// paints at full width with no slide-in; only user toggles animate.
const useTransitionsArmed = () => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return armed;
};

export const APFilterPanelTrigger = ({
  open,
  onToggle,
  activeCount,
  'data-testid': testId = 'filter-panel',
}: {
  open: boolean;
  onToggle: () => void;
  activeCount: number;
  'data-testid'?: string;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      aria-expanded={open}
      onClick={onToggle}
      data-testid={`${testId}-trigger`}
    >
      <FilterLinear aria-hidden="true" />
      {t('CommonText.filters')}
      {activeCount > 0 && (
        <Badge variant="secondary" className="tabular-nums">
          {activeCount}
        </Badge>
      )}
    </Button>
  );
};

export interface APFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Clear the page's filters (draftless — controls apply live). */
  onReset: () => void;
  /** Disable Reset while nothing is applied. */
  resetDisabled?: boolean;
  /** Already-translated title; defaults to the "Filters" label. */
  title?: ReactNode;
  /** Filter controls, live-bound to the table's filter state. */
  children: ReactNode;
  'data-testid'?: string;
}

export const APFilterPanel = ({
  open,
  onOpenChange,
  onReset,
  resetDisabled,
  title,
  children,
  'data-testid': testId = 'filter-panel',
}: APFilterPanelProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const transitionsArmed = useTransitionsArmed();
  // Desktop push-panel open collapses the app sidebar (restore-on-close
  // semantics live in the hook); the <lg Sheet overlay never touches it.
  useSidebarAutoCollapse(open && isDesktop);

  // Reset lives in the HEADER row (dev decision, BG-feedback6): the panel
  // body scrolls, so a footer Reset sat below the fold on long filter sets.
  const resetButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={onReset}
      disabled={resetDisabled}
      data-testid={`${testId}-reset`}
    >
      {t('CommonText.reset')}
    </Button>
  );

  if (!isDesktop) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="end" data-testid={testId}>
          {/* pe-10 clears the sheet's built-in absolute close X. */}
          <SheetHeader
            className="flex-row items-center justify-between pe-10"
            data-testid={`${testId}-header`}
          >
            <SheetTitle>{title ?? t('CommonText.filters')}</SheetTitle>
            {resetButton}
          </SheetHeader>
          {/* pt-2/-mt-2: scroll-padding headroom so the FIRST field's floated
              label (rides the border, D5 extension) isn't clipped by the
              scroll container; the negative margin keeps the visual rhythm. */}
          <div className="-mt-2 flex flex-1 flex-col gap-3 overflow-y-auto px-4 pt-2 pb-4">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    // Animated width on the outer box; the inner column keeps its full width
    // so the controls don't reflow while the panel slides.
    <aside
      aria-hidden={!open}
      data-testid={testId}
      className={cn(
        // `visibility` in the transition list flips at the end of the close
        // animation and removes the hidden panel from the tab order.
        // w-[20rem], not w-80 — v3 App.scss ships an unlayered `.w-80 { width:
        // 80px }` that clobbers the Tailwind utility (deviations #7).
        'shrink-0 overflow-hidden',
        transitionsArmed
          ? 'transition-[width,opacity,visibility] duration-200 ease-out motion-reduce:transition-none'
          : 'transition-none',
        // eslint-disable-next-line no-restricted-syntax -- v1 .w-80 !important clobbers the scale name until 7.5 (MIGRATION.md deviation #7)
        open ? 'visible w-[20rem] opacity-100' : 'invisible w-0 opacity-0'
      )}
    >
      {/* A proper floating card on the canvas (LU3) — matches the list card
          beside it; the < lg Sheet branch keeps its own overlay treatment. */}
      <div className="ms-4 flex h-full w-76 flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between" data-testid={`${testId}-header`}>
          <h3 className="text-sm font-medium">{title ?? t('CommonText.filters')}</h3>
          <div className="flex shrink-0 items-center gap-1">
            {resetButton}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('CommonText.close')}
              onClick={() => onOpenChange(false)}
              data-testid={`${testId}-close`}
            >
              <CloseCircleLinear aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>
        {/* pt-2/-mt-2: scroll-padding headroom for the first field's floated
            label (D5 extension) — see the Sheet branch note above. */}
        <div className="-mt-2 flex flex-1 flex-col gap-3 overflow-y-auto pt-2">{children}</div>
      </div>
    </aside>
  );
};
